import React, { useState, useEffect } from 'react';
import { Eye } from 'lucide-react';
import { apiFetch } from '../../services/api';

/**
 * ExecutionHistoryTable: paginated table of workflow executions.
 *
 * Features:
 * - Paginated with page size selector
 * - Status badges (RUNNING=blue, COMPLETED=green, FAILED=red)
 * - View action to open run detail modal
 * - Polling for RUNNING executions (refresh every 5s)
 *
 * Props:
 *   workflowId: UUID
 *   onViewExecution: callback(runId)
 */
export const ExecutionHistoryTable = ({ workflowId, onViewExecution }) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasRunningExecutions, setHasRunningExecutions] = useState(false);

  const fetchExecutions = async () => {
    try {
      setLoading(true);
      // Canonical run-list endpoint on the unified WorkflowController
      // surface — see refactor #36. Server may ignore page/size today
      // (returns last 20 by default) but we keep the params on the
      // wire so we don't have to revisit this when pagination lands.
      // Routed through the shared apiFetch so auth/refresh/CSRF/cookie
      // handling is identical to every other call (no hand-rolled bearer).
      const res = await apiFetch(`/api/v1/workflows/${workflowId}/runs?page=${page}&size=${pageSize}`);
      const result = res.data;
      setData(result);
      const hasRunning = result?.content?.some(e => e.status === 'RUNNING');
      setHasRunningExecutions(hasRunning);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch executions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExecutions();
  }, [workflowId, page, pageSize]);

  // Poll for running executions every 5 seconds
  useEffect(() => {
    if (!hasRunningExecutions) return;
    const interval = setInterval(fetchExecutions, 5000);
    return () => clearInterval(interval);
  }, [hasRunningExecutions]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'RUNNING':
        return 'bg-blue-100 text-blue-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    return Math.round(ms / 1000) + 's';
  };

  if (loading && !data) {
    return <div className="p-8 text-center text-gray-500">Loading executions...</div>;
  }

  if (error) {
    return <div className="p-8 bg-red-50 border border-red-200 rounded text-red-800">{error}</div>;
  }

  if (!data || data.totalElements === 0) {
    return <div className="p-8 text-center text-gray-500">No executions yet. Start one with "Trigger New Run".</div>;
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Started</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Duration</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Run ID</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.content.map((exec, idx) => (
              <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                <td className="px-4 py-2 text-sm text-gray-700">{formatTime(exec.startedAt)}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{formatDuration(exec.durationMs)}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(exec.status)}`}>
                    {exec.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-sm font-mono text-gray-700">
                  {exec.runId.substring(0, 8)}...
                </td>
                <td className="px-4 py-2 text-center">
                  <button
                    onClick={() => onViewExecution(exec.runId)}
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition"
                    title="View execution details"
                  >
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 p-4 bg-gray-50 rounded">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Rows per page:</label>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setPage(0);
            }}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div className="text-sm text-gray-700">
          Page {page + 1} of {data.totalPages || 1} ({data.totalElements || 0} total)
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            Previous
          </button>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= (data.totalPages - 1)}
            className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};
